import { useAuthStore } from '@/store/authStore';

export type UserRole = 'cbs_admin' | 'portal';

interface UsePermissionReturn {
  has: (code: string) => boolean;
  hasAny: (codes: readonly string[]) => boolean;
  hasAll: (codes: readonly string[]) => boolean;
  hasRole: (role: UserRole) => boolean;
  permissions: readonly string[];
  role: string | null;
}

/**
 * Read the current user's permissions and role from the auth store.
 *
 * Mirrors the backend authorization model:
 * - `has` / `hasAny` / `hasAll` check membership of permission codes.
 * - `hasRole` checks the user's role for role-only-gated UI (dashboard,
 *   operator management, etc.).
 *
 * Returns `false` for every check when the user is unauthenticated, so
 * consumers do not need to guard for that case.
 */
export function usePermission(): UsePermissionReturn {
  const user = useAuthStore((s) => s.user);
  const permissions = user?.permissions ?? [];
  const role = user?.role ?? null;

  return {
    has: (code) => permissions.includes(code),
    hasAny: (codes) => codes.some((c) => permissions.includes(c)),
    hasAll: (codes) => codes.every((c) => permissions.includes(c)),
    hasRole: (r) => role === r,
    permissions,
    role,
  };
}
